import { z } from 'zod';

import { IsoDateTimeSchema, NullableIsoDateTimeSchema } from '../common/datetime';
import { TimezoneSchema } from '../common/fields';
import {
  AttendanceMarkId,
  CompanyId,
  DeviceId,
  InvitationId,
  SiteId,
  WorkerId,
} from '../common/ids';
import { ManagedBySchema } from '../common/integration';
import { CursorQuerySchema, pageSchema } from '../common/pagination';
import { DevicePlatform, DeviceStatus, DisplayMode } from '../staff/devices';
import { AttendanceActionSchema, AttendanceStateSchema, MarkSiteRefSchema } from '../staff/vpp';

import { SiteScopeItemSchema } from './admins';

// ---------------------------------------------------------------------------
// Enums (data-model.md · workers, worker_invitations)
// ---------------------------------------------------------------------------

export const DocumentType = z.enum(['DNI', 'CE', 'PASSPORT', 'OTHER']);
export type DocumentType = z.infer<typeof DocumentType>;

export const WorkerStatus = z.enum(['PENDING_ACTIVATION', 'ACTIVE', 'INACTIVE']);
export type WorkerStatus = z.infer<typeof WorkerStatus>;

export const InvitationStatus = z.enum(['PENDING', 'USED', 'EXPIRED', 'REVOKED']);
export type InvitationStatus = z.infer<typeof InvitationStatus>;

/**
 * Motivo de revocación de una invitación (`worker_invitations.revoke_reason`). 005 · QA-6 añade
 * `VERIFICATION_FAILED`: se agotaron los intentos de verificación del documento al activar.
 */
export const INVITATION_REVOKE_REASONS = [
  'SUPERSEDED',
  'WORKER_DEACTIVATED',
  'ADMIN',
  'VERIFICATION_FAILED',
] as const;
export const InvitationRevokeReasonSchema = z.enum(INVITATION_REVOKE_REASONS);
export type InvitationRevokeReason = z.infer<typeof InvitationRevokeReasonSchema>;

// ---------------------------------------------------------------------------
// Campos (rangos de data-model.md · workers)
// ---------------------------------------------------------------------------

export const WorkerNameSchema = z.string().min(1).max(120);
/** 4–32 caracteres. La API normaliza (trim, mayúsculas) antes de guardar y comparar. */
export const DocumentNumberSchema = z.string().min(4).max(32);
export const PHONE_PATTERN = /^[0-9+() -]{6,20}$/;
export const PhoneSchema = z.string().regex(PHONE_PATTERN, {
  message: 'Teléfono inválido: entre 6 y 20 caracteres (dígitos, +, paréntesis, espacio o guion)',
});
export const WorkerEmailSchema = z.email().max(254);
export const InternalCodeSchema = z.string().min(1).max(40);

/** Motivo opcional de una baja/revocación/rechazo (`{ reason? }`). */
export const ReasonSchema = z.string().min(1).max(500);

const workerIdentityFields = {
  first_names: WorkerNameSchema,
  last_names: WorkerNameSchema,
  document_type: DocumentType,
  document_number: DocumentNumberSchema,
  phone: PhoneSchema,
  email: WorkerEmailSchema.nullable(),
  internal_code: InternalCodeSchema.nullable(),
};

// ---------------------------------------------------------------------------
// Entidad (contracts/admin-workers.md · Worker)
// ---------------------------------------------------------------------------

/** Referencia mínima a una sede: `{ id, name }`. */
export const SiteRefSchema = z.object({
  id: SiteId,
  name: z.string(),
});
export type SiteRef = z.infer<typeof SiteRefSchema>;

/** Dispositivo vinculado tal como lo ve el admin. */
export const WorkerDeviceSummarySchema = z.object({
  id: DeviceId,
  platform: DevicePlatform,
  display_mode: DisplayMode,
  status: DeviceStatus,
  enrolled_at: IsoDateTimeSchema,
  last_seen_at: NullableIsoDateTimeSchema,
  // 005 · QA-5: sistema, navegador y modelo. La API siempre los manda (derivados del user-agent y
  // de los client hints; `null` si no se pudieron deducir). Opcionales en el tipo solo para no
  // romper a consumidores que construyen el DTO a mano.
  /** `Android`, `iOS`, `iPadOS`, `Windows`, `macOS`, `Linux`, `HarmonyOS`… */
  os_name: z.string().nullable().optional(),
  /** `14`, `18.2`… */
  os_version: z.string().nullable().optional(),
  /** `Chrome`, `Safari`, `Samsung Internet`, `Firefox`, `Edge`, `Huawei Browser`… */
  browser_name: z.string().nullable().optional(),
  /** Versión mayor (`140`); en Safari de iOS, la del sistema (`18.2`). */
  browser_version: z.string().nullable().optional(),
  /** Modelo del teléfono (client hints de Chromium en Android); nunca en iPhone. */
  model: z.string().nullable().optional(),
});
export type WorkerDeviceSummary = z.infer<typeof WorkerDeviceSummarySchema>;

/** Invitación más reciente: `{ id, status, expires_at }`. */
export const WorkerInvitationSummarySchema = z.object({
  id: InvitationId,
  status: InvitationStatus,
  expires_at: IsoDateTimeSchema,
  /** 005 · QA-6: motivo si está `REVOKED` (`VERIFICATION_FAILED` = bloqueada por verificación). */
  revoke_reason: InvitationRevokeReasonSchema.nullable().optional(),
  /**
   * 005 · QA-9: `true` si el trabajador está **en pleno registro** con esta invitación (documento
   * verificado o código de traspaso vigente y sin usar). El Admin lo usa para advertir antes de
   * generar una invitación nueva, sin exponer fechas ni hashes de verificación/traspaso.
   */
  activation_in_progress: z.boolean(),
});
export type WorkerInvitationSummary = z.infer<typeof WorkerInvitationSummarySchema>;

// ---------------------------------------------------------------------------
// Asistencia en la ficha (005 · FR-018, contracts/admin-attendance.md §8)
// ---------------------------------------------------------------------------

/**
 * Estado + origen de una marca, para el badge (FR-024). Lo calcula el servidor para que tabla,
 * detalle, ficha y exportación digan lo mismo: `VOIDED` si anulada, `SUPERSEDED` si reemplazada,
 * `MANUAL` si `source = 'MANUAL'` y vigente; si no, el `status` (`VALID`/`FLAGGED`).
 *
 * Vive aquí y no en `attendance.ts` para no cerrar el ciclo de imports
 * `attendance → terminals → workers → attendance` (`terminals.ts` importa `ReasonSchema` de este
 * archivo). Se exporta por el barrel igual que el resto; `attendance.ts` lo importa de aquí.
 */
export const MarkDisplayStatusSchema = z.enum([
  'VALID',
  'FLAGGED',
  'MANUAL',
  'SUPERSEDED',
  'VOIDED',
]);
export type MarkDisplayStatus = z.infer<typeof MarkDisplayStatusSchema>;

/** Estado de asistencia del trabajador ("Dentro desde 08:42"). */
export const WorkerAttendanceStateSchema = z.object({
  /** `IN | OUT | UNRESOLVED` (`staff/vpp.ts`). */
  state: AttendanceStateSchema,
  /** Hora de la última marca vigente; `null` si nunca marcó. */
  since: NullableIsoDateTimeSchema,
  /** El turno abierto superó `max_open_shift_hours`. */
  open_shift_exceeded: z.boolean(),
});
export type WorkerAttendanceState = z.infer<typeof WorkerAttendanceStateSchema>;

/** Última marca vigente del trabajador. Horas con `site_timezone` (la de la marca), no `site.timezone`. */
export const WorkerLastMarkSchema = z.object({
  id: AttendanceMarkId,
  type: AttendanceActionSchema,
  server_timestamp: IsoDateTimeSchema,
  site: MarkSiteRefSchema,
  site_timezone: TimezoneSchema,
  display_status: MarkDisplayStatusSchema,
});
export type WorkerLastMark = z.infer<typeof WorkerLastMarkSchema>;

export const WorkerSchema = z.object({
  id: WorkerId,
  company_id: CompanyId,
  ...workerIdentityFields,
  status: WorkerStatus,
  activated_at: NullableIsoDateTimeSchema,
  deactivated_at: NullableIsoDateTimeSchema,
  primary_site: SiteRefSchema,
  /** Todas las sedes autorizadas (incluida la principal), con su estado. */
  sites: z.array(SiteScopeItemSchema),
  /** Dispositivo `ACTIVE` vinculado, o `null`. */
  device: WorkerDeviceSummarySchema.nullable(),
  /** La invitación más reciente, o `null` si nunca se generó una. */
  invitation: WorkerInvitationSummarySchema.nullable(),
  pending_rebind_requests: z.number().int().nonnegative(),
  /**
   * 005 · FR-018: solo el detalle (`GET /workers/{id}`) lo rellena, y solo si el actor tiene
   * `attendance.read`. El listado y la exportación de trabajadores lo siguen devolviendo `null`.
   */
  attendance_state: WorkerAttendanceStateSchema.nullable(),
  /** 005 · FR-018: mismas reglas que `attendance_state`. */
  last_mark: WorkerLastMarkSchema.nullable(),
  /** 005 · retención: momento en que se anonimizó (FR-021); `null` si no lo está. */
  anonymized_at: NullableIsoDateTimeSchema,
  /**
   * 009 · vínculo con el sistema externo: campos de solo lectura (R-14) y cese programado (R-13);
   * `null` = trabajador solo de Fermin. Obligatorio desde US6 (T053, `workers/mapper.ts`).
   */
  managed_by: ManagedBySchema.nullable(),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema,
});
export type Worker = z.infer<typeof WorkerSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const workerSitesFields = {
  /** ≥ 1 sede de la empresa (y del scope del actor); lo verifica la API → `VALIDATION_ERROR`. */
  site_ids: z.array(SiteId).min(1),
  primary_site_id: SiteId,
};

function primarySiteIsAuthorized(body: { site_ids?: string[]; primary_site_id?: string }): boolean {
  if (!body.site_ids || !body.primary_site_id) {
    return true;
  }
  return body.site_ids.includes(body.primary_site_id);
}

const PRIMARY_SITE_ISSUE = {
  message: 'La sede principal debe estar entre las sedes autorizadas',
  path: ['primary_site_id'],
};

/**
 * `POST /workers`: `{ first_names, last_names, document_type, document_number, phone, email?,
 * internal_code?, site_ids (≥1), primary_site_id }`.
 */
export const CreateWorkerBodySchema = z
  .object({
    first_names: WorkerNameSchema,
    last_names: WorkerNameSchema,
    document_type: DocumentType,
    document_number: DocumentNumberSchema,
    phone: PhoneSchema,
    email: WorkerEmailSchema.nullable().optional(),
    internal_code: InternalCodeSchema.nullable().optional(),
    ...workerSitesFields,
  })
  .refine(primarySiteIsAuthorized, PRIMARY_SITE_ISSUE);
export type CreateWorkerBody = z.infer<typeof CreateWorkerBodySchema>;

/**
 * `PATCH /workers/{id}`: parcial de `{ first_names, last_names, document_type, document_number,
 * phone, email, internal_code, site_ids, primary_site_id }`.
 */
export const UpdateWorkerBodySchema = z
  .object({ ...workerIdentityFields, ...workerSitesFields })
  .partial()
  .refine(primarySiteIsAuthorized, PRIMARY_SITE_ISSUE);
export type UpdateWorkerBody = z.infer<typeof UpdateWorkerBodySchema>;

const workerFilterFields = {
  /** Prefijo en nombres, apellidos o documento. */
  q: z.string().min(1).max(120).optional(),
  site_id: SiteId.optional(),
  status: WorkerStatus.optional(),
};

/** `GET /workers?q&site_id&status&cursor&limit` */
export const WorkerListQuerySchema = CursorQuerySchema.extend(workerFilterFields);
export type WorkerListQuery = z.infer<typeof WorkerListQuerySchema>;

/** `GET /workers/export?q&site_id&status` (mismos filtros, sin paginación). */
export const WorkerExportQuerySchema = z.object(workerFilterFields);
export type WorkerExportQuery = z.infer<typeof WorkerExportQuerySchema>;

/** Parámetro de ruta `{id}` de trabajador. */
export const WorkerIdParamsSchema = z.object({ id: WorkerId });
export type WorkerIdParams = z.infer<typeof WorkerIdParamsSchema>;

/** Parámetros de `POST /workers/{id}/invitations/{invId}/revoke`. */
export const WorkerInvitationParamsSchema = z.object({ id: WorkerId, invId: InvitationId });
export type WorkerInvitationParams = z.infer<typeof WorkerInvitationParamsSchema>;

export const INVITATION_TTL_HOURS_MIN = 1;
export const INVITATION_TTL_HOURS_MAX = 168;
/** Valor por defecto de la API (`INVITATION_TTL_HOURS_DEFAULT` en env) cuando el body no lo trae. */
export const INVITATION_TTL_HOURS_DEFAULT = 72;

/** `POST /workers/{id}/invitations`: `{ ttl_hours?: 1–168 }`. */
export const CreateInvitationBodySchema = z.object({
  ttl_hours: z
    .number()
    .int()
    .min(INVITATION_TTL_HOURS_MIN)
    .max(INVITATION_TTL_HOURS_MAX)
    .optional(),
});
export type CreateInvitationBody = z.infer<typeof CreateInvitationBodySchema>;

/** `POST /workers/{id}/device/revoke`: `{ reason? }`. */
export const RevokeDeviceBodySchema = z.object({
  reason: ReasonSchema.optional(),
});
export type RevokeDeviceBody = z.infer<typeof RevokeDeviceBodySchema>;

/** `POST /workers/{id}/deactivate`: `{ reason? }`. */
export const DeactivateWorkerBodySchema = z.object({
  reason: ReasonSchema.optional(),
});
export type DeactivateWorkerBody = z.infer<typeof DeactivateWorkerBodySchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

/** `200 Page<Worker>` */
export const WorkerListResponseSchema = pageSchema(WorkerSchema);
export type WorkerListResponse = z.infer<typeof WorkerListResponseSchema>;

/**
 * `201 { invitation: { id, status, expires_at }, invitation_link }` de `POST /workers/{id}/invitations`.
 * `invitation_link` = `${APP_ORIGIN_STAFF}/activar/${token}`; se muestra una vez y nunca se loguea.
 */
export const CreateInvitationResponseSchema = z.object({
  invitation: WorkerInvitationSummarySchema,
  invitation_link: z.url(),
});
export type CreateInvitationResponse = z.infer<typeof CreateInvitationResponseSchema>;

/** `200 { id, status: 'REVOKED' }` de `POST /workers/{id}/invitations/{invId}/revoke`. */
export const RevokeInvitationResponseSchema = z.object({
  id: InvitationId,
  status: z.literal('REVOKED'),
});
export type RevokeInvitationResponse = z.infer<typeof RevokeInvitationResponseSchema>;
